import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { usePhotosDeps } from './context';
import type { Photo, PhotoKind, SignedUrl } from './schemas';

/**
 * The TanStack Query layer.
 *
 * **A signed URL is cached for less time than it lives.** Rule 10 gives a
 * token fifteen minutes; these queries hold one for ten, so a gallery left
 * open does not hand a browser an `<img src>` that has just died. The
 * alternative — refetching on every render — would mint a capability per
 * paint and fill the processing log with disclosures nobody made.
 */
export const photoKeys = {
  all: ['photos'] as const,
  session: (sessionId: string) => [...photoKeys.all, 'session', sessionId] as const,
  mine: () => [...photoKeys.all, 'mine'] as const,
  url: (photoId: string, own: boolean) => [...photoKeys.all, 'url', photoId, own] as const,
};

/** Ten minutes, against rule 10's fifteen. See the module docstring. */
export const URL_STALE_MS = 600_000;

/** Every photograph on a session, deleted ones included (rule 7). */
export function useSessionPhotos(sessionId: string): UseQueryResult<Photo[]> {
  const { api, canCapture } = usePhotosDeps();
  return useQuery({
    queryKey: photoKeys.session(sessionId),
    queryFn: () => api.listForSession(sessionId),
    enabled: canCapture && sessionId !== '',
  });
}

/** The client's own gallery (rule 6). */
export function useMyPhotos(): UseQueryResult<Photo[]> {
  const { api } = usePhotosDeps();
  return useQuery({ queryKey: photoKeys.mine(), queryFn: () => api.myPhotos() });
}

/**
 * A capability for one photograph's bytes.
 *
 * `own` picks the route, and therefore who the ownership check runs against:
 * a client asking for their own photograph goes through `/me/photos/{id}/url`,
 * which refuses one that is not theirs.
 */
export function useSignedUrl(photoId: string, own: boolean): UseQueryResult<SignedUrl> {
  const { api } = usePhotosDeps();
  return useQuery({
    queryKey: photoKeys.url(photoId, own),
    queryFn: () => (own ? api.mySignedUrl(photoId) : api.signedUrl(photoId)),
    staleTime: URL_STALE_MS,
    enabled: photoId !== '',
  });
}

/** Record one photograph against a session. */
export function useCapturePhoto(
  sessionId: string,
): UseMutationResult<Photo, Error, { file: File; client_id: string; kind: PhotoKind }> {
  const { api } = usePhotosDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ file, ...body }) => api.capture(sessionId, file, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: photoKeys.session(sessionId) });
    },
  });
}

/**
 * Remove one (`session.manage`).
 *
 * No session id: a deletion changes the session's list, the client's gallery
 * and any URL cached for that photograph, so it invalidates the whole
 * namespace. A narrower key would leave a tombstone showing as a live photo
 * on whichever of the three screens was not named.
 */
export function useRemovePhoto(): UseMutationResult<void, Error, string> {
  const { api } = usePhotosDeps();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) => api.remove(photoId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: photoKeys.all });
    },
  });
}
